import { MaskType } from "@/composables/maskingUtils";
import { DisplayField } from "@/types/types";

export enum BorrowerTypes {
  PrimaryBorrower = 1,
  CoBorrower = 2,
  Student = 3,
}

export class BorrowerTypeIds {
  public static get PrimaryBorrower(): number {
    return BorrowerTypes.PrimaryBorrower;
  }

  public static get CoBorrower(): number {
    return BorrowerTypes.CoBorrower;
  }

  public static get Student(): number {
    return BorrowerTypes.Student;
  }
}

export interface FormField {
  name: string;
  label: string;
  type: string;
  value: any;
  width: string;
  options?: any;
  showOnAdd?: boolean;
  showOnEdit: boolean;
  disabledOnEdit: boolean;
  disabledOnAdd?: boolean;
  maskType?: MaskType;
}

export interface LoanDetails {
  value: any | undefined;
  loan: Loan;
  documentEntityId: number;
  availableToBorrow: AvailableToBorrow | null;
  isClosedForDraws: boolean;
  locStatusHumanized: string | null;
  indexFormatted: string | null;
  marginFormatted: string | null;
  floorFormatted: string | null;
  ceilingFormatted: string | null;
  applicationUrl: string | null;
  rateTypeHumanized: string | null;
  applicationInterestRateFormatted: string | null;
  servicerInterestRateFormatted: string | null;
  repaymentTypeHumanized: string | null;
  lenderName: string | null;
  loanStatusHumanized: string | null;
  disbursedAmountFormatted: string | null;
  loanAmountTitle: string | null;
  loanAmount: number;
  loanAmountFormatted: string | null;
  repaymentTermFormatted: string | null;
  programName: string | null;
  loanStatusAtUtcFormatted: string;
  loanStatusReasonHumanized: string;
  loanStageHumanized: string;
  filteredKeyDates: KeyDate[];
  program: any; // todo
  borrowers: Borrower[];
  servicerPartner: ServicerPartner | null;
  servicerLoanData: ServicerLoanData | null;
  locMetadata: LocMetadata | null;
  annualReviews: AnnualReview[];
  locLimitLoanAmount?: DisplayField | null;
  applicationCompletionDateFormatted: string;
  minimumDrawAmount?: MinimumDrawAmountResponse | null;
  isParentLOC: boolean;
  student?: any;
	loanId: number;
	applicationLoanId: number;
}

export interface MinimumDrawAmountResponse {
  minimumInitialDrawAmount?: number;
  minimumSubsequentDrawAmount?: number;
  minimumInitialDrawAmountDisplay: string;
  minimumSubsequentDrawAmountDisplay: string;
}

export interface MinimumDrawAmount {
  amount: number;
  display: string;
}
export interface Loan {
  id: number;
  referenceId: string;
  programTypeId: number;
  productType: number;
  lenderId: number;
  programAltCode: string | null;
}

export interface Borrower {
  id: number;
  initials: string;
  firstName: string;
  lastName: string;
  applicantId: number | null;
  borrowerTypeId: number;
  fullName: string;
  phoneNumber: string | null;
  emailAddress: string | null;
  nationalIdNumber: string;
  nationalIdNumberFormatted: string;
  dateofBirth: Date;
  dateOfBirthFormatted: string;
  address: string;
  documentPersonId: number;
}

export interface BorrowerSlim {
  applicantId: number;
  firstName: string;
  id: number;
  lastName: string;
  typeId: number;
}

export interface ServicerPartner {
  name: string;
  adminLoginUrl: string | null;
}

export interface ServicerLoanData {
  servicerLoanId: string;
}

export interface KeyDate {
  keyDateTypeHumanized: string;
  dateFormatted: string;
}

export interface AvailableToBorrow {
  availableToDrawAmount: number;
  drawsEnabled: boolean;
}

export enum DisbursementStatuses {
  PreDisbursementCancellation = 2,
  OnHold = 3,
  CertifiedOnHold = 4,
  ScheduledDisbursement = 5,
  DisbursementPending = 6,
  Disbursed = 7,
  PostDisbursementCancellation = 8,
  PostDisbursementPartialCancellation = 9,
}

export class Draw {
  id = 0;
	drawId = 0;
  certificationId = 0;
  drawStatusTypeId = 1;
  certificationMethodId = 1;
  enrollmentStatus = 1;
  cluid: string | null = null;
  sequenceNumber: string | null = null;
  schoolLevelTypeId: number | null = null;
  enrollmentTermId: number | null = null;
  requestedAmount: number | null = null;
  certifiedAmount: number | null = null;
  anticipatedGraduationDate: string | null = "";
  loanPeriodStartDate: string | null = "";
  loanPeriodEndDate: string | null = "";
  schoolCode: string | null = null;
  schoolBranch: string | null = null;
  schoolDetailsId: number | null = null;
  requestedAmountFormatted: string | null = "";
  createdBy: string | null = null;
  createdAtUtc: string | null = null;

  constructor(initialValues?: Partial<Draw>) {
    if (initialValues) {
      Object.assign(this, initialValues);
    }
  }
}

export interface ServiceType {
  id: number;
  name: string | null;
  description?: string | null;
}

export interface School {
  id: number;
  schoolDetailsId: number;
  schoolName: string;
  schoolDOEId: string;
  schoolBranchDOEId: string;
}

export enum DrawStatuses {
  CertificationPending = 1,
  SentForCertification,
  Certified,
  SchoolUnableToCertify,
  PartiallyDisbursed,
  FullyDisbursed,
  Terminated,
  LenderHold,
  ReadyForCertification,
}

export const ValidDrawStatusIds = [
  DrawStatuses.CertificationPending,
  DrawStatuses.SentForCertification,
  DrawStatuses.SchoolUnableToCertify,
  DrawStatuses.LenderHold,
  DrawStatuses.Certified,
  DrawStatuses.Terminated,
];

export const DrawStatusesToAllowUpdateCertMethod: ReadonlyArray<DrawStatuses> =
  [
    DrawStatuses.SentForCertification,
    DrawStatuses.CertificationPending,
    DrawStatuses.SchoolUnableToCertify,
    DrawStatuses.LenderHold,
    DrawStatuses.Certified,
    DrawStatuses.Terminated,
  ];

export const DrawStatusesToAllowUpdateLoanPeriodDates: ReadonlyArray<DrawStatuses> =
  DrawStatusesToAllowUpdateCertMethod;

export class Disbursement {
  id = 0;
  disbursementTypeId = 1;
  disbursementStatusTypeId = 1;
  drawDisbursementStatusReasonTypeId: number | null = null;
  sequenceNumber: string | null = null;
  certifiedAmount: number | null = null;
  disbursedAmount: number | null = null;
  transactionDate: string | null = "";
  disbursedAmountFormatted: string | null = "";
  certifiedAmountFormatted?: string | null = "";
  disbursementDateDisplay?: string | null = "";
  transactionDateDisplay: string | null = "";

  constructor(initialValues?: Partial<Disbursement>) {
    if (initialValues) {
      Object.assign(this, initialValues);
    }
  }
}

export interface LocMetadata {
  loanId: number;
  drawEnableOverrideUntilUtc: string | null;
  drawOverrideAmount: number | null;
}

export const ValidDisbursementStatusIds = [
  DisbursementStatuses.PreDisbursementCancellation,
  DisbursementStatuses.OnHold, // school hold
  DisbursementStatuses.CertifiedOnHold,
  DisbursementStatuses.ScheduledDisbursement,
  DisbursementStatuses.DisbursementPending,
  DisbursementStatuses.Disbursed,
];

export interface Comment {
  id: number;
  entityId: number;
  commentType: number;
  comment: string | null;
  userDisplayName: string | null;
  createdAtUtc: Date;
  initials: string | null;
  createdAtFormatted: string | null;
}

export interface PayoffDetails {
  id: number;
  batchId: string;
  payoffDisbursementStatusId: number;
  payoffDisbursementStatusReasonId: number;
  payoffDisbursementTypeId: number;
  disbursementAmount: number;
  payoffFees: number;
  adjustedDisbursementAmount: number;
  adjustedFees: number;
  lenderName: string;
  subServicerName: string;
  servicerName: string;
  attentionTo: string;
  transactionDate: string | null;
  settleDate: string | null;
  disbursementStatusHumanized: string;
  disbursementStatusReasonHumanized: string;
  disbursementTypeHumanized: string;
  disbursementAmountFormatted: string;
  adjustedDisbursementAmountFormatted: string;
  transactionDateFormatted: string;
  settleDateFormatted: string;
  subServicerAddress: SubServicerAddress | null;
  financialInstitution: FinancialInstitution | null;
  accountNumber: string;
  payoffPostDisbursementChanges: PayoffPostDisbursementChangeModel[];
}

export interface SubServicerAddress {
  name: string;
  attentionTo: AttentionTo | null;
  address1: string | null;
  address2: string | null;
  city: string;
  state: string;
  zipCode: string;
  zipCodeExtension: string;
}

export interface AttentionTo {
  name: string;
}

export interface FinancialInstitution {
  name: string;
  accountingNumber: string | null;
  routingNumber: string | null;
}

export interface PayoffPostDisbursementChange {
  adjustedAmount: number;
  adjustedFeeAmount: number;
  refundProcessingDate: string;
  refundSettleDate: string;
  refundEffectiveDate: string;
  createdAtUtc?: Date | null;
  updatedBy?: string | null;
  payoffId?: number | null;
}

export interface PayoffPostDisbursementChangeModel {
  adjustedAmount: string;
  adjustedFeeAmount: string;
  createdDate: string;
  effectiveDate: string;
  payoffId: number;
  processingDate: string;
  payoffDisbursementStatusReasonId: number;
  payoffDisbursementStatusReasonHumanized: string;
  updatedBy: string;
}

export interface LoanDocument {
  id: number;
  name: string;
  uploadAtUtc: Date;
  documentTypeId: number;
  borrowerId: number;
  uploadAtUtcFormatted: string;
  documentTypeHumanized: string;
}

export interface UpdateDocumentRequest {
  documentTypeId: number;
  borrowerId: number;
}

export interface DocumentProperties {
  pageCount: number;
}

export interface SplitDocumentRequest {
  documentId: number;
  entityTypeId: number;
  options: SplitDocumentOptions[];
}

export interface SplitDocumentOptions {
  // todo: update app-centric naming in C# models
  documentType: number;
  applicantId: number;
  startIndex: number;
  endIndex: number;
  applicantViewable: boolean;
}

export interface DateOption {
  id: number;
  name: string;
}

export interface AnnualReview {
  id: number;
  loanId: number;
  annualReviewYear: number;
}

export interface AnnualReviewDetails {
  id: number;
  loanId: number;
  annualReviewYear: number;
  annualReviewStatusId: number;
  annualReviewStatusHumanized: string;
  annualReviewStatusClass: string;
  hasCourtesyRepull: boolean;
  applicationTypeId: number;
  statusDateFormatted: string;
  createdDateFormatted: string;
  batchedDateFormatted: string;
  creditPulledDateFormatted: string;
  initialStatusDateFormatted: string;
  finalStatusDateFormatted: string;
  borrowerCourtesyRepullDateFormatted: string;
  coBorrowerCourtesyRepullDateFormatted: string;
  annualReviewStatusLedgers: AnnualReviewStatusLedger[];
  annualReviewOverrides: AnnualReviewOverride[];
}

export interface AnnualReviewStatusLedger {
  id: number;
  annualReviewStatusId: number;
  annualReviewStatusHumanized: string;
  createdAtUtcFormatted: string;
}

export interface AnnualReviewOverride {
  id: number;
  annualReviewId: number;
  overrideTypeId: number;
}

export interface AnnualReviewCreditDetails {
  knockouts: KnockoutModel[];
  alerts: AlertModel[];
  borrowerCreditPullTypeId: number;
  coBorrowerCreditPullTypeId: number;
}

export interface OverrideCreditDetails {
  borrowerKnockouts: BorrowerKnockoutModel[];
  coBorrowerKnockouts: BorrowerKnockoutModel[];
}

export interface KnockoutModel {
  knockoutTypeId: number;
  knockoutName: string;
  borrowerKnockout: BorrowerKnockoutModel;
  coKnockout: BorrowerKnockoutModel;
}

export interface BorrowerKnockoutModel {
  decisionTypeId: number;
  value: string;
  decision: string;
  decisionCssClass: string;
  knockoutTypeId: number;
  knockoutName: string;
}

export interface AlertModel {
  alertTypeId: number;
  alertName: string;
  borrowerAlert: BorrowerAlertModel;
  coAlert: BorrowerAlertModel;
}

export interface BorrowerAlertModel {
  statusTypeId: number;
  statusName: string;
  statusCssClass: string;
}

export interface BorrowerCreditReportModel {
  id: number;
  borrowerTypeId: number;
  rawCreditReportId: number;
  name: string;
}

export class LoanBorrower {
  id: number;
  borrowerTypeId: number;
  firstName: string;
  lastName: string;

  constructor(
    id: number,
    borrowerTypeId: number,
    firstName: string,
    lastName: string
  ) {
    this.id = id;
    this.borrowerTypeId = borrowerTypeId;
    this.firstName = firstName;
    this.lastName = lastName;
  }

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }
}

export interface ValidateDrawDisbursementDateRequest {
  disbursementDate: string | Date;
  productTypeId: number;
  disbursementMethodTypeId: number;
}
export interface BorrowerTypeModel {
  id: number | null;
  name: string | null;
  description: string | null;
}

export interface SuffixTypeModel {
  id: number | null;
  name: string | null;
  description: string | null;
}
export interface BorrowerContactModel {
  id: number | null;
  phoneNumberTypeId: number;
  preferredContactTypeId: number | null;
  emailAddress: string | null;
}
export interface BorrowerModel {
  id: number | null;
  applicantId: number | null;
  borrowerTypeId: number;
  suffixTypeId: number | null;
  citizenshipStatusTypeId: number | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  normalizedFirstName: string | null;
  normalizedMiddleName: string | null;
  normalizedLastName: string | null;
  dateOfBirth: string | Date;
  userId: string | null;
  nationalIdNumber: string;
  borrowerType: null | BorrowerTypeModel;
  borrowerContact: null | BorrowerContactModel;
  borrowerAddress: null | BorrowerAddressModel;
  // borrowerDriversLicense: null | BorrowerDriversLicenseModel;
}
export interface BorrowerAddressModel {
  id: number;
  addressTypeId: number;
  addressStateId: number;
  addressCountryId: number;
  addressLineOne: string | null;
  addressLineTwo: string | null;
  city: string | null;
  postalCode: string | null;
  postalCodeExtension: string | null;
}

export interface UpdateBorrowerRequest {
  loanId: number;
	borrowerId: number;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | Date | null;
	suffixTypeId: number | null;
	citizenshipStatusTypeId: number | null;
  borrowerAddress: UpdateBorrowerAddressModel | null;
  borrowerEncryptedData: UpdateBorrowerEncryptedDataModel | null;
  borrowerContact: UpdateBorrowerContactModel | null;
  borrowerDriversLicense: UpdateBorrowerDriversLicenseModel | null;
}

export interface UpdateBorrowerContactModel {
  preferredContactTypeId: number | null;
  emailAddress: string | null;
}

export interface UpdateBorrowerContactModel {
  preferredContactTypeId: number | null;
  emailAddress: string | null;
  phoneNumber: string | null;
}

export interface UpdateBorrowerAddressModel {
  addressTypeId: number | null;
  addressStateId: number | null;
  addressCountryId: number | null;
  addressLineOne: string | null;
  addressLineTwo: string | null;
  city: string | null;
  postalCode: string | null;
  postalCodeExtension: string | null;
}

export interface UpdateBorrowerDriversLicenseModel {
  state: string | null;
  number: string | null;
}

export interface UpdateBorrowerEncryptedDataModel {
  nationalIdNumber: string | null;
	nationalIdNumberTypeId: number | null;
}

export interface UpdateBorrowerRequestV1 {
  id: number;
  suffixTypeId: number | null;
  citizenshipStatusTypeId: number | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  userId: string | null;
  dateOfBirth: string | Date | null;
  nationalIdNumber: string | null;
  borrowerContact: UpdateBorrowerContactModel | null;
  borrowerAddress: UpdateBorrowerAddressModel | null;
  borrowerDriversLicense: UpdateBorrowerDriversLicenseModel | null;
}

export interface StudentContactModel {
  studentId: number;
  contactTypeId: number;
  phoneNumberTypeId: number;
  emailAddress: string | null;
  phoneNumber: string | null;
  phoneNumberExtension: string | null;
}
export interface StudentAddressModel {
  studentId: number;
  addressTypeId: number;
  addressStateTypeId: number;
  addressCountryTypeId: number;
  addressLineOne: string | null;
  addressLineTwo: string | null;
  city: string | null;
  postalCode: string | null;
  postalCodeExtension: string | null;
}
export interface StudentDriversLicenseModel {
  studentId: number;
  number: string | null;
  state: string | null;
}

export interface StudentEncryptedDataModel {
  studentId: number;
  nationalIdNumberTypeId: number | null;
  nationalIdNumber: string | null;
}

export interface StudentModel {
  id: number;
  applicationLoanId: number;
  suffixTypeId: null | number;
  citizenshipStatusTypeId: number;
  applicantId: number | null;
  userId: number | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | Date;
  studentAddress: null | StudentAddressModel;
  studentContact: null | StudentContactModel;
  studentDriversLicense: null | StudentDriversLicenseModel;
  studentEncryptedData: null | StudentEncryptedDataModel;
}
